import { AxiosError } from 'axios'
import { createApiClient } from '@fdg01/shared/api-client'
import { TIMEOUTS } from './constants'
import { parseApiError } from './errorHandler'
import { logger } from './logger'
import { getTenantSlug } from './authRedirect'
import { createClient as createSupabaseClient } from '@/lib/supabase/client'

// NOTA DE AISLAMIENTO: Esta es la única instancia axios para los 3 modos
// (barbershop, studio, interno). La resolución del tenant depende de:
//  - barbershop/studio: el header `host` que Traefik forwardea a la API.
//  - interno: el header `X-Tenant-Slug` (inyectado por getTenantSlug()).
// Si se accede a la API directamente sin Traefik, no hay resolución por host.
// No crear instancias axios adicionales por modo — el interceptor de request
// ya maneja la inyección de X-Tenant-Slug automáticamente.

const API_URL = process.env.NEXT_PUBLIC_API_URL || '/api/v1'

// Create axios instance via shared createApiClient
const axiosInstance = createApiClient({
  baseURL: API_URL,
  authMode: 'bearer', // Supabase access token injected as Bearer header
  timeout: TIMEOUTS.API_REQUEST,
})

/**
 * Get headers for fetch calls that need auth + X-Tenant-Slug.
 * Use this when you can't use the axios instance (e.g., FormData uploads).
 *
 * Now async — reads the Supabase session to inject the Bearer token.
 */
export async function getFetchHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {}
  const supabase = createSupabaseClient()
  const { data: { session } } = await supabase.auth.getSession()
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`
  }
  const slug = getTenantSlug()
  if (slug) {
    headers['X-Tenant-Slug'] = slug
  }
  return headers
}

// Request interceptor - inject Supabase access token + X-Tenant-Slug
axiosInstance.interceptors.request.use(
  async (config) => {
    // Inject Supabase access token as Bearer header
    const supabase = createSupabaseClient()
    const { data: { session } } = await supabase.auth.getSession()
    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`
    }
    // If data is FormData, remove Content-Type header to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    // Inject X-Tenant-Slug on the centralized admin host so the backend can
    // resolve the tenant (subdomain-based resolution doesn't apply here).
    const slug = getTenantSlug()
    if (slug) {
      config.headers['X-Tenant-Slug'] = slug
    }
    return config
  },
  (error) => {
    logger.error('Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor - supabase-js handles token refresh and session expiry.
// No more auth:logout event — onAuthStateChange in AuthContext handles SIGNED_OUT.
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const parsedError = parseApiError(error)
    // supabase-js auto-refreshes expired tokens. If we get 401 here, the
    // session is truly invalid — onAuthStateChange in AuthContext will fire
    // SIGNED_OUT and redirect to login via useProtectedRoute.
    logger.error('API Error:', {
      endpoint: error.config?.url,
      method: error.config?.method,
      status: parsedError.status,
      message: parsedError.message,
    })
    return Promise.reject(error)
  }
)

export const api = {
  async get(endpoint: string, params?: Record<string, string>) {
    const response = await axiosInstance.get(endpoint, params ? { params } : undefined)
    return response.data
  },

  async post(endpoint: string, data: unknown) {
    const response = await axiosInstance.post(endpoint, data)
    return response.data
  },

  async put(endpoint: string, data: unknown) {
    const response = await axiosInstance.put(endpoint, data)
    return response.data
  },

  async patch(endpoint: string, data: unknown) {
    const response = await axiosInstance.patch(endpoint, data)
    return response.data
  },

  async delete(endpoint: string) {
    const response = await axiosInstance.delete(endpoint)
    return response.data
  },
}
